// Spec 078 US5 / FR-035 — a barra da rota ambiente: nome da linha, relógio, contagens por status e
// alertas (dot + texto, nunca só cor), badge de staleness em destaque e tela cheia. Tipografia
// para TV: ≥ 32 px em 1080p (text-4xl). DOM puro — sem three.js — testável em jsdom.
import * as React from "react";
import { Maximize2, WifiOff } from "lucide-react";
import { Dot, type DotTone } from "@/components/ui/dot";
import { formatSince, type Staleness } from "../hooks/useStaleness";
import type { LineSceneResponse } from "@/types/scene.types";

export interface AmbientCounts {
  running: number;
  stopped: number;
  idle: number;
  nodata: number;
  alerts: number;
}

export function ambientCounts(scene: LineSceneResponse | undefined): AmbientCounts {
  const c: AmbientCounts = { running: 0, stopped: 0, idle: 0, nodata: 0, alerts: 0 };
  for (const m of scene?.machines ?? []) {
    if (m.status === "RUNNING") c.running += 1;
    else if (m.status === "STOPPED") c.stopped += 1;
    else if (m.status === "IDLE") c.idle += 1;
    else c.nodata += 1;
    // Spec 080 — conta a PARADA TÉRMICA em aberto, com ou sem alarme (FR-009): fora de produção
    // não nasce alarme nenhum, e é justamente aí que a tela ambiente é a única coisa olhando.
    if (m.heat) c.alerts += 1;
  }
  return c;
}

const clock = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" });
const CLOCK_TICK_MS = 15_000;

function useClock(): string {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, []);
  return clock.format(now);
}

function useFullscreen(): { supported: boolean; active: boolean; toggle: () => void } {
  const supported =
    typeof document !== "undefined" && "requestFullscreen" in document.documentElement;
  const [active, setActive] = React.useState(() => Boolean(document.fullscreenElement));
  React.useEffect(() => {
    const on = () => setActive(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  const toggle = React.useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void document.documentElement.requestFullscreen?.();
  }, []);
  return { supported, active, toggle };
}

interface CountProps {
  tone: DotTone;
  value: number;
  label: string;
}
function Count({ tone, value, label }: CountProps): React.ReactElement {
  return (
    <span className="inline-flex items-center gap-2.5" data-testid={`ambient-count-${label}`}>
      <Dot tone={tone} size={14} halo={false} />
      <span className="font-semibold tabular-nums">{value}</span>
      <span className="text-text-inverse-secondary">{label}</span>
    </span>
  );
}

export interface AmbientBarProps {
  scene: LineSceneResponse | undefined;
  staleness: Staleness;
  /** nome de fallback enquanto a cena não chegou */
  lineName?: string;
}

export function AmbientBar({ scene, staleness, lineName }: AmbientBarProps): React.ReactElement {
  const time = useClock();
  const fs = useFullscreen();
  const c = ambientCounts(scene);
  const name = scene?.meta.lineName ?? lineName ?? "—";
  return (
    <header
      className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-b border-border-inverse bg-bg-inverse px-8 py-4 text-4xl text-text-inverse"
      data-testid="ambient-bar"
    >
      <div className="flex min-w-0 items-center gap-6">
        <h1 className="truncate font-bold tracking-tight">{name}</h1>
        {staleness.stale ? (
          <span
            className="inline-flex items-center gap-3 rounded-2xl bg-rd-warning-500 px-4 py-1 text-3xl font-semibold text-black"
            role="status"
            data-testid="ambient-stale"
          >
            <WifiOff className="h-8 w-8" aria-hidden />
            Sem atualização desde {formatSince(staleness.since)}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
        <Count tone="success" value={c.running} label="rodando" />
        <Count tone="danger" value={c.stopped} label="paradas" />
        {c.idle > 0 ? <Count tone="warning" value={c.idle} label="ociosas" /> : null}
        <Count tone="neutral" value={c.nodata} label="sem dados" />
        <Count tone="warning" value={c.alerts} label="alertas" />
        <time className="font-semibold tabular-nums" data-testid="ambient-clock">
          {time}
        </time>
        {fs.supported ? (
          <button
            type="button"
            onClick={fs.toggle}
            aria-label={fs.active ? "Sair da tela cheia" : "Tela cheia"}
            aria-pressed={fs.active}
            className="rounded-xl border border-border-inverse p-2 text-text-inverse-secondary hover:text-text-inverse"
          >
            <Maximize2 className="h-8 w-8" aria-hidden />
          </button>
        ) : null}
      </div>
    </header>
  );
}
